import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Store, Tag, Inbox, Quote, Images, Archive, Sparkles, BadgeCheck, Scale, ScrollText, Loader2, Save, RefreshCw, ExternalLink, CheckCircle2, XCircle, AlertTriangle,
} from "lucide-react";
import {
  PLAN_LABELS, SITE_DEFAULTS, formatTry, sitePricingSchema,
  type PlanId, type RoutePath, type SitePricing,
} from "@wedding/shared";
import { adminPlansApi, adminSiteApi, type AdminSiteData } from "../lib/api";
import { AppLink } from "../lib/route-primitives";
import { paths, type SiteTab } from "../lib/paths";
import { cn } from "../lib/utils";
import { LeadsTab } from "./site-leads";
import { ProofTab } from "./site-proof";
import { MediaTab } from "./site-media";
import { RetentionTab } from "./site-retention";
import { DemoTab } from "./site-demo";
import { SalesTab } from "./site-sales";
import { LegalTab } from "./site-legal";
import { AddonPricingCard } from "./site-addons";
import { LedgerTab } from "./site-ledger";

/**
 * "Satış & Site" — landing'in (wedding.mertbal.com) yönetilebilir içeriği.
 *
 * Her sekme `platform_settings` içindeki bir `site:<bölüm>` kaydını düzenler
 * (worker: `routes/admin-site.ts`, audit'li). Landing değeri en geç ~1–2 dk'da
 * görür (worker 30 sn isolate önbelleği + 60 sn tarayıcı önbelleği).
 * Plan: docs/plans/2026-10-13-satis-site-yonetimi.md — sekmeler fazlarla gelir.
 */

const WEB_BASE =
  (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

const TABS: { id: SiteTab; labelKey: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "pricing", labelKey: "siteAdmin.tabs.pricing", icon: Tag },
  { id: "leads", labelKey: "siteAdmin.tabs.leads", icon: Inbox },
  { id: "proof", labelKey: "siteAdmin.tabs.proof", icon: Quote },
  { id: "media", labelKey: "siteAdmin.tabs.media", icon: Images },
  { id: "retention", labelKey: "siteAdmin.tabs.retention", icon: Archive },
  { id: "demo", labelKey: "siteAdmin.tabs.demo", icon: Sparkles },
  { id: "sales", labelKey: "siteAdmin.tabs.sales", icon: BadgeCheck },
  { id: "legal", labelKey: "siteAdmin.tabs.legal", icon: Scale },
  { id: "ledger", labelKey: "siteAdmin.tabs.ledger", icon: ScrollText },
];

const toTab = (v: string | null): SiteTab => TABS.find((tab) => tab.id === v)?.id ?? "pricing";

const PLAN_IDS: PlanId[] = ["free", "pro", "max"];

// Toast — settings.tsx deseni (başarı + hata), her mutasyon için zorunlu.
function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 3200);
    return () => clearTimeout(timer);
  }, [onDone]);
  return (
    <div role="status" aria-live="polite" className={cn(
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
      "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
      ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
    )}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

// Flex-based toggle — control-center ile aynı.
function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        "flex items-center h-6 w-11 rounded-full border-2 p-0.5 shrink-0",
        "transition-[background-color,border-color] duration-300 cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        "hover:opacity-80",
        checked ? "bg-primary/30 border-primary/50 justify-end" : "bg-muted border-border justify-start",
      )}
    >
      <span className={cn(
        "h-4 w-4 rounded-full shadow-sm transition-colors duration-300 shrink-0",
        checked ? "bg-primary" : "bg-muted-foreground/40",
      )} />
    </button>
  );
}

function TabLink({ active, to, icon: Icon, label }: {
  active: boolean; to: RoutePath; icon: React.ComponentType<{ className?: string }>; label: string;
}) {
  return (
    <AppLink to={to} aria-current={active ? "page" : undefined} replace
      className={cn(
        "inline-flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-t-lg",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
      )}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {label}
    </AppLink>
  );
}

export function SitePage() {
  const { t } = useTranslation();
  // Sekme URL'de (`?tab=`): yenileme / paylaşılan bağlantı aynı sekmeye düşsün.
  const [params] = useSearchParams();
  const tab = toTab(params.get("tab"));
  const [settings, setSettings] = useState<AdminSiteData | null>(null);
  // Sunucudan her yeni değer (yükle / kaydet) sekmeyi yeniden kurar → taslak
  // o değerden başlar. Effect ile senkronlamak, ilk düzenlemeyi ezebilirdi.
  const [version, setVersion] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);
  const clearToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const res = await adminSiteApi.get();
      if (!res.data) throw new Error("empty");
      setSettings(res.data);
      setVersion((v) => v + 1);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-foreground flex items-center gap-2">
            <Store className="h-6 w-6 text-primary" aria-hidden="true" />
            {t("siteAdmin.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t("siteAdmin.description")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={`${WEB_BASE}/#plans`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:border-primary/30 transition-all cursor-pointer"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{t("siteAdmin.openSite")}</span>
          </a>
          <button
            type="button"
            onClick={() => void load()}
            aria-label={t("siteAdmin.reload")}
            className="p-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:border-primary/30 transition-all cursor-pointer"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Sekmeler LİNK (durum URL'de) — sms-center.tsx ile aynı gerekçe. */}
      <nav aria-label={t("siteAdmin.title")} className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((item) => (
          <TabLink key={item.id} active={tab === item.id} to={paths.siteTab(item.id)}
            icon={item.icon} label={t(item.labelKey)} />
        ))}
      </nav>

      {/* Teklifler kendi verisini yükler (site ayarlarından bağımsız). */}
      {tab === "leads" ? (
        <LeadsTab bounds={settings?.pricing.bounds ?? SITE_DEFAULTS.pricing.bounds} showToast={showToast} />
      ) : tab === "ledger" ? (
        <LedgerTab showToast={showToast} />
      ) : loadError ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 px-5 py-6 flex items-center gap-4">
          <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
          <p className="text-sm text-foreground flex-1">{t("siteAdmin.loadFailed")}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-full border border-border px-4 py-2 text-sm hover:bg-primary/5 cursor-pointer"
          >
            {t("siteAdmin.retry")}
          </button>
        </div>
      ) : !settings ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : tab === "legal" ? (
        <LegalTab
          key={version}
          initial={settings.legal}
          onSaved={(legal) => setSettings((s) => (s ? { ...s, legal } : s))}
          showToast={showToast}
        />
      ) : tab === "sales" ? (
        <SalesTab
          signup={settings.signup}
          legal={settings.legal}
          pricing={settings.pricing}
          onSignupChange={(signup) => setSettings((s) => (s ? { ...s, signup } : s))}
          showToast={showToast}
        />
      ) : tab === "demo" ? (
        <DemoTab
          demo={settings.demo}
          onDemoChange={(demo) => setSettings((s) => (s ? { ...s, demo } : s))}
          showToast={showToast}
        />
      ) : tab === "retention" ? (
        <RetentionTab
          key={version}
          initial={settings.retention}
          onSaved={(retention) => setSettings((s) => (s ? { ...s, retention } : s))}
          showToast={showToast}
        />
      ) : tab === "media" ? (
        <MediaTab
          media={settings.media}
          onChange={(media) => setSettings((s) => (s ? { ...s, media } : s))}
          reload={() => void load()}
          showToast={showToast}
        />
      ) : tab === "proof" ? (
        <ProofTab
          key={version}
          settings={settings}
          // Remount YOK: iki kartın taslakları bağımsız; biri kaydedilince
          // diğerindeki kaydedilmemiş düzenleme kaybolmasın.
          onSaved={(patch) => setSettings((s) => (s ? { ...s, ...patch } : s))}
          showToast={showToast}
        />
      ) : (
        (
          <div className="space-y-6">
            <PricingTab
              key={version}
              saved={settings.pricing}
              onSaved={(pricing) => {
                setSettings((s) => (s ? { ...s, pricing } : s));
                setVersion((v) => v + 1);
              }}
              showToast={showToast}
            />
            {/* QR ekstra kayıt paketleri (2026-10-15) — `site:addons` */}
            <AddonPricingCard
              saved={settings.addons ?? SITE_DEFAULTS.addons}
              onSaved={(addons) => setSettings((s) => (s ? { ...s, addons } : s))}
              showToast={showToast}
            />
          </div>
        )
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={clearToast} />}
    </div>
  );
}

// ─── Fiyatlar ─────────────────────────────────────────────────────────────

type PlanPriceRow = { slug: string; label: string; price_try?: number | null; max_guests_per_wedding: number; is_active: number };

/**
 * Fiyatlar sekmesi (2026-10-16): yalnız "fiyatlar sitede görünsün mü" + serbest not. Paketin
 * fiyatı ve TÜM sayıları paket tanımında (Paketler sayfası) — misafir aralığı / fiyat kademesi
 * kaldırıldı ("300 misafirlik paket Pro'dur; 500 misafir olup Max olmamak olmaz"). Eski kademe
 * değerleri kayıtta kalır (eski talep etiketleri için) ama hiçbir yerde satışa girmez.
 */
function PricingTab({ saved, onSaved, showToast }: {
  saved: SitePricing;
  onSaved: (p: SitePricing) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(saved.show);
  const [note, setNote] = useState(saved.note);
  const [saving, setSaving] = useState(false);
  const dirty = show !== saved.show || note.trim() !== saved.note;

  // Paket tanımlarındaki fiyat + misafir (salt okunur özet; düzenleme Paketler sayfasında).
  const [plans, setPlans] = useState<PlanPriceRow[] | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await adminPlansApi.get<PlanPriceRow[]>("/");
        if (alive) setPlans((res.data ?? []).filter((p) => p.is_active && (PLAN_IDS as readonly string[]).includes(p.slug)));
      } catch {
        if (alive) setPlans([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleSave = async () => {
    const value = sitePricingSchema.safeParse({ ...saved, show, note: note.trim() });
    if (!value.success) {
      showToast(t("siteAdmin.invalid"), false);
      return;
    }
    setSaving(true);
    try {
      const res = await adminSiteApi.put("pricing", value.data);
      onSaved(res.data ?? value.data);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      showToast(t("siteAdmin.saveFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setSaving(false);
    }
  };

  const nf = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-6">
      {/* Görünürlük */}
      <div className="rounded-2xl border border-border bg-card px-5 py-4 flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{t("siteAdmin.pricing.showTitle")}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.pricing.showDesc")}</p>
        </div>
        <span className={cn("text-xs font-semibold", show ? "text-green-500" : "text-muted-foreground")}>
          {show ? t("controlCenter.enabled") : t("controlCenter.disabled")}
        </span>
        <Toggle checked={show} onChange={setShow} label={t("siteAdmin.pricing.showTitle")} />
      </div>

      {/* Paket fiyatları — tek kaynak: paket tanımı */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="pricing-packages">
        <div className="px-5 py-4 border-b border-border bg-muted/30 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-foreground">{t("siteAdmin.pricing.packagesTitle")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.pricing.packagesHint")}</p>
          </div>
          <AppLink
            to={paths.plans}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{t("siteAdmin.pricing.editPackages")}</span>
          </AppLink>
        </div>
        {plans === null ? (
          <div className="px-5 py-4"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
        ) : (
          <ul className="divide-y divide-border">
            {PLAN_IDS.map((id) => {
              const p = plans.find((x) => x.slug === id);
              const price = p?.price_try;
              return (
                <li key={id} data-testid={`pricing-package-${id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
                  <span className="font-semibold text-foreground">{p?.label ?? PLAN_LABELS[id]}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {p ? t("siteAdmin.pricing.packageGuests", { n: nf.format(p.max_guests_per_wedding) }) : "—"}
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {price === undefined || price === null ? t("landing.plans.priceQuote") : formatTry(price, i18n.language)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Not */}
      <div className="rounded-2xl border border-border bg-card px-5 py-4 space-y-2">
        <label htmlFor="site-pricing-note" className="text-sm font-semibold text-foreground">
          {t("siteAdmin.pricing.noteTitle")}
        </label>
        <p className="text-xs text-muted-foreground">{t("siteAdmin.pricing.noteHint")}</p>
        <textarea
          id="site-pricing-note"
          rows={3}
          maxLength={200}
          value={note}
          placeholder={t("landing.plans.priceNote")}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50"
        />
        <p className="text-right text-[10px] text-muted-foreground tabular-nums">{note.length}/200</p>
      </div>

      {/* Eylemler */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {dirty && <span className="mr-auto text-xs font-medium text-amber-600 dark:text-amber-400">{t("siteAdmin.unsaved")}</span>}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? t("siteAdmin.saving") : t("siteAdmin.save")}
        </button>
      </div>
    </div>
  );
}
